// Explorer state persistence (Phase 3a finish) — PURE.
//
// The drill-down explorer's group-by chips, Flat/Tree mode, AND visible stat
// columns survive reloads via the generic User.gridPreferences mechanism (gridKey
// 'reporting-explorer'), same store as the accounts/campaign grids. The store's
// shape is { activePresetId, presets: [{ id, name, visibleColumns: string[] }] } —
// so the explorer state is encoded as tokens in a single reserved preset's
// visibleColumns array: `dim:<dimension>` (in order) + `mode:<flat|tree>` +
// `col:<statKey>` (in order). Reuses the existing PATCH /api/me/preferences
// endpoint with no server change. Legacy prefs (pre-column-selector) carry no
// `col:` tokens → the default stat set is applied.

import type { GridPreferences } from '../../types/user';
import type { ReportingDimension } from '../../types/reporting';
import {
  BREAKDOWN_DEFAULT_STAT_COLUMNS,
  canonicalizeGroupBy,
  sanitizeColumns,
  type BreakdownColumnKey,
} from './breakdownColumns';

export const REPORTING_EXPLORER_GRID_KEY = 'reporting-explorer';

// The single reserved preset id we stash explorer state under (never 'default',
// which the preferences schema reserves for the built-in).
const STATE_PRESET_ID = 'state';

export type ExplorerMode = 'flat' | 'tree';

export interface ExplorerState {
  groupBy: ReportingDimension[]; // canonical depth order
  mode: ExplorerMode;
  statColumns: BreakdownColumnKey[]; // visible columns (stat + attribute; catalogue order)
}

// groupBy=['campaign'] reproduces the old campaign table — the sensible default.
export const DEFAULT_EXPLORER_STATE: ExplorerState = {
  groupBy: ['campaign'],
  mode: 'flat',
  statColumns: [...BREAKDOWN_DEFAULT_STAT_COLUMNS],
};

const VALID_DIMS: ReportingDimension[] = ['account', 'campaign', 'adset', 'ad'];

// Encode the state → a GridPreferences envelope the preferences endpoint accepts.
export function encodeExplorerState(state: ExplorerState): GridPreferences {
  const groupBy = canonicalizeGroupBy(state.groupBy);
  const statColumns = sanitizeColumns(state.statColumns);
  const tokens = [
    ...groupBy.map((d) => `dim:${d}`),
    `mode:${state.mode}`,
    ...statColumns.map((c) => `col:${c}`),
  ];
  return {
    activePresetId: STATE_PRESET_ID,
    presets: [{ id: STATE_PRESET_ID, name: 'Explorer state', visibleColumns: tokens }],
  };
}

// Decode a stored GridPreferences envelope → the explorer state (defaults when
// absent / malformed — never throws). Empty/invalid group-by → the default
// dimension; missing/invalid columns → the default stat set (so a legacy pref or
// a cleared selection never yields an empty pivot).
export function decodeExplorerState(prefs: GridPreferences | undefined): ExplorerState {
  const preset = prefs?.presets.find((p) => p.id === STATE_PRESET_ID);
  if (!preset) return cloneDefault();
  const tokens = preset.visibleColumns;

  const groupBy = canonicalizeGroupBy(
    tokens
      .filter((t) => t.startsWith('dim:'))
      .map((t) => t.slice(4))
      .filter((d): d is ReportingDimension => (VALID_DIMS as string[]).includes(d)),
  );
  const modeToken = tokens.find((t) => t.startsWith('mode:'))?.slice(5);
  const mode: ExplorerMode = modeToken === 'tree' ? 'tree' : 'flat';
  const colTokens = tokens.filter((t) => t.startsWith('col:')).map((t) => t.slice(4));

  return {
    groupBy: groupBy.length > 0 ? groupBy : [...DEFAULT_EXPLORER_STATE.groupBy],
    mode,
    // sanitizeColumns([]) → the default set, so legacy prefs (no col: tokens)
    // and a hypothetical cleared selection both fall back to defaults.
    statColumns: sanitizeColumns(colTokens),
  };
}

function cloneDefault(): ExplorerState {
  return {
    groupBy: [...DEFAULT_EXPLORER_STATE.groupBy],
    mode: DEFAULT_EXPLORER_STATE.mode,
    statColumns: [...DEFAULT_EXPLORER_STATE.statColumns],
  };
}
